import React, { useCallback, useEffect, useState } from "react";
import API from "../utils/api";
import { downloadRowsAsXlsx } from "../utils/excelDownload";

function todayIST() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}

function shiftDays(dateKey, days) {
  const d = new Date(`${dateKey}T00:00:00+05:30`);
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}

function mondayOf(dateKey) {
  const d = new Date(`${dateKey}T00:00:00+05:30`);
  const day = d.getDay();
  const back = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - back);
  return d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}

function monthStart(dateKey) {
  return `${dateKey.slice(0, 7)}-01`;
}

function money(n) {
  const v = Number(n) || 0;
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(1)} Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)} L`;
  if (!v) return "—";
  return `₹${v.toLocaleString("en-IN")}`;
}

const PRESETS = [
  { id: "today", label: "Today", range: (t) => [t, t] },
  { id: "yesterday", label: "Yesterday", range: (t) => [shiftDays(t, -1), shiftDays(t, -1)] },
  { id: "week", label: "This week", range: (t) => [mondayOf(t), t] },
  { id: "month", label: "This month", range: (t) => [monthStart(t), t] },
  { id: "7", label: "Last 7 days", range: (t) => [shiftDays(t, -6), t] },
  { id: "30", label: "Last 30 days", range: (t) => [shiftDays(t, -29), t] },
];

function Stat({ label, value, hint, color, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 16,
        padding: "16px 14px",
        textAlign: "left",
        cursor: onClick ? "pointer" : "default",
        fontFamily: "inherit",
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: "0.04em", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 900, color: color || "#0f172a", marginTop: 4, lineHeight: 1.1 }}>{value}</div>
      {hint && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>{hint}</div>}
    </button>
  );
}

export default function CommandCenter({ accent = "#E31E24", onOpenTab, onOpenPerson, drillItems }) {
  const today = todayIST();
  const [preset, setPreset] = useState("today");
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (fromDate, toDate) => {
    setLoading(true);
    setError("");
    try {
      const res = await API.get(`/reports/command-center?from=${fromDate}&to=${toDate}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || "Could not load the dashboard");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(from, to); }, [from, to, load]);

  const applyPreset = (item) => {
    const [start, end] = item.range(todayIST());
    setPreset(item.id);
    setFrom(start);
    setTo(end);
  };

  const t = data?.totals || {};
  const isToday = from === to && to === today;
  const tempTotal = (t.hot || 0) + (t.warm || 0) + (t.cold || 0) || 1;

  const exportExcel = () => {
    if (!data) return;
    const rows = [
      ["Period", `${data.from} to ${data.to}`],
      ["On field", t.on_field],
      ["Institution visits", t.institutions],
      ["Different institutions", t.distinct_institutions],
      ["Route km (login → visits → log-off)", t.km],
      ["Visits", t.visits],
      ["Management visits", t.management_visits],
      ["Reception visits", t.reception_visits],
      ["Leads on book", t.leads],
      ["Hot", t.hot],
      ["Warm", t.warm],
      ["Cold", t.cold],
      ["Follow-ups completed", t.followups_done],
      ["Overdue follow-ups", t.overdue],
      ["Due today", t.due_today],
      ["Leads with no follow-up date", t.leads_without_followup],
      ["Pipeline value", t.pipeline_value],
      [],
      ["Name", "Field days", "Institution visits", "Route km", "Visits", "Leads", "Hot", "Warm", "Cold", "Follow-ups done", "Overdue"],
      ...data.people.map((p) => [
        p.name, p.field_days, p.institutions, p.km, p.visits, p.leads, p.hot, p.warm, p.cold, p.followups_done, p.overdue,
      ]),
    ];
    const peopleHeaderIndex = rows.findIndex(row => row[0] === "Name");
    downloadRowsAsXlsx({
      filename: `crm-command-${data.from}-to-${data.to}.xlsx`,
      sheetName: "Command Centre",
      title: "CRM Command Centre",
      subtitleRows: rows.slice(0, peopleHeaderIndex).filter(row => row.length).map(row => row.filter(value => value !== undefined).join(": ")),
      headers: rows[peopleHeaderIndex],
      rows: rows.slice(peopleHeaderIndex + 1),
    });
  };

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 16 }}>
        {PRESETS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => applyPreset(item)}
            style={{
              padding: "7px 12px",
              borderRadius: 999,
              border: "none",
              fontFamily: "inherit",
              fontWeight: 700,
              fontSize: 12,
              cursor: "pointer",
              background: preset === item.id ? accent : "#fff",
              color: preset === item.id ? "#fff" : "#475569",
              boxShadow: "0 0 0 1px #e2e8f0",
            }}
          >
            {item.label}
          </button>
        ))}
        <input
          type="date"
          value={from}
          onChange={(e) => { setPreset("custom"); setFrom(e.target.value); }}
          aria-label="From date"
          style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: "6px 8px", fontFamily: "inherit", fontSize: 12 }}
        />
        <span style={{ color: "#94a3b8", fontSize: 12 }}>to</span>
        <input
          type="date"
          value={to}
          onChange={(e) => { setPreset("custom"); setTo(e.target.value); }}
          aria-label="To date"
          style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: "6px 8px", fontFamily: "inherit", fontSize: 12 }}
        />
        <button
          type="button"
          onClick={exportExcel}
          disabled={!data}
          style={{ marginLeft: "auto", padding: "7px 12px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: "pointer", color: "#334155" }}
        >
          Download Excel
        </button>
      </div>

      {loading && <div style={{ textAlign: "center", padding: 48, color: "#94a3b8" }}>Loading the team picture…</div>}
      {error && !loading && <div style={{ background: "#fef2f2", color: "#b91c1c", borderRadius: 12, padding: 14, fontSize: 13 }}>{error}</div>}

      {!loading && data && (
        <>
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>
            {isToday ? "Today on the field" : `Field activity ${data.from} → ${data.to}`}
            {" · "}Lead book is live (not limited to this range)
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 12 }}>
            <Stat
              label="On the field"
              value={t.on_field}
              hint={isToday ? `${t.team_size} in the team` : `${t.field_days} field-days`}
              color={accent}
              onClick={() => onOpenTab?.("field")}
            />
            <Stat
              label="Institution visits"
              value={t.institutions}
              hint={`${t.distinct_institutions ?? t.institutions} different · ${t.management_visits} met, ${t.reception_visits} reception`}
              onClick={() => onOpenTab?.("field")}
            />
            <Stat
              label="Route km"
              value={t.km || "—"}
              hint={t.km ? "Login → visits → log-off" : "No GPS yet for this range"}
            />
            <Stat
              label="Leads on name"
              value={t.leads}
              hint={t.pipeline_value ? `Pipeline ${money(t.pipeline_value)}` : "Open book"}
              onClick={() => onOpenTab?.("leads")}
            />
            <Stat
              label="Followed up"
              value={t.followups_done}
              hint={`${t.due_today} due today · ${t.overdue} overdue`}
              color={t.overdue ? "#c2410c" : "#15803d"}
              onClick={() => onOpenTab?.("followups")}
            />
          </div>

          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, padding: 16, marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a", marginBottom: 10 }}>Temperature</div>
            <div style={{ display: "flex", height: 12, borderRadius: 999, overflow: "hidden", background: "#f1f5f9", marginBottom: 12 }}>
              <div style={{ width: `${(t.hot / tempTotal) * 100}%`, background: "#dc2626" }} />
              <div style={{ width: `${(t.warm / tempTotal) * 100}%`, background: "#d97706" }} />
              <div style={{ width: `${(t.cold / tempTotal) * 100}%`, background: "#2563eb" }} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
              {[
                ["Hot", t.hot, "#dc2626", "#fee2e2"],
                ["Warm", t.warm, "#b45309", "#fef3c7"],
                ["Cold", t.cold, "#1d4ed8", "#dbeafe"],
              ].map(([label, value, fg, bg]) => (
                <div key={label} style={{ background: bg, borderRadius: 12, padding: "12px 10px", textAlign: "center" }}>
                  <div style={{ fontSize: 22, fontWeight: 900, color: fg }}>{value}</div>
                  <div style={{ fontSize: 11, fontWeight: 800, color: fg }}>{label}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12, fontSize: 12, color: "#475569" }}>
              <span>{t.leads_without_followup} leads have no next follow-up date</span>
              <span>·</span>
              <span>{t.overdue_leads} leads are past their follow-up date</span>
              <span>·</span>
              <span>{t.due_week} follow-ups in the next 7 days</span>
              {(t.won || t.lost) ? <span>· {t.won} won / {t.lost} lost on the book</span> : null}
            </div>
          </div>

          {(t.overdue > 0 || (isToday && t.not_in_today > 0)) && (
            <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 16, padding: 14, marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#9a3412", marginBottom: 6 }}>Needs attention</div>
              {t.overdue > 0 && (
                <button type="button" onClick={() => onOpenTab?.("followups")} style={{ display: "block", background: "none", border: "none", padding: 0, fontFamily: "inherit", fontSize: 13, color: "#9a3412", cursor: "pointer", marginBottom: 4 }}>
                  {t.overdue} overdue follow-ups — open Follow-ups
                </button>
              )}
              {isToday && data.not_in_today?.length > 0 && (
                <div style={{ fontSize: 13, color: "#9a3412" }}>
                  Not checked in today: {data.not_in_today.map((p) => p.name).slice(0, 8).join(", ")}
                  {data.not_in_today.length > 8 ? ` +${data.not_in_today.length - 8}` : ""}
                </div>
              )}
            </div>
          )}

          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, overflow: "hidden", marginBottom: 12 }}>
            <div style={{ padding: "14px 16px", borderBottom: "1px solid #f1f5f9", fontSize: 13, fontWeight: 800 }}>
              By person {from === to ? "— tap Photos & map for GPS, photos, start and finish" : ""}
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 720 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "#64748b" }}>
                    {["Name", "Field days", "Inst. visits", "Route km", "Visits", "Leads", "Hot / Warm / Cold", "Followed up", "Overdue", "Map"].map((h) => (
                      <th key={h} style={{ padding: "8px 12px", fontWeight: 700, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.people.map((p) => (
                    <tr key={p.user_id}>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #f8fafc", fontWeight: 700, color: "#0f172a" }}>
                        {p.name}
                        {isToday && p.on_field_today && <span style={{ marginLeft: 6, fontSize: 10, color: accent, fontWeight: 800 }}>FIELD</span>}
                        {isToday && !p.checked_in_today && <span style={{ marginLeft: 6, fontSize: 10, color: "#dc2626", fontWeight: 800 }}>OUT</span>}
                      </td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #f8fafc" }}>{p.field_days}</td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #f8fafc" }}>{p.institutions}</td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #f8fafc" }}>{p.km || "—"}</td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #f8fafc" }}>{p.visits}</td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #f8fafc" }}>{p.leads}</td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #f8fafc" }}>{p.hot} / {p.warm} / {p.cold}</td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #f8fafc" }}>{p.followups_done}</td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #f8fafc", color: p.overdue ? "#c2410c" : "#64748b", fontWeight: p.overdue ? 800 : 500 }}>{p.overdue || "—"}</td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #f8fafc" }}>
                        {p.visits > 0 && (
                          <button
                            type="button"
                            onClick={() => onOpenPerson?.(p, from === to ? from : to)}
                            style={{ padding: "5px 9px", borderRadius: 8, border: `1px solid ${accent}`, background: `${accent}12`, color: accent, fontFamily: "inherit", fontSize: 11, fontWeight: 800, cursor: "pointer" }}
                          >
                            Photos & map
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {(drillItems || [
              ["field", "Visits & maps"],
              ["leads", "Lead list"],
              ["followups", "Follow-ups"],
              ["briefing", "Evening briefing"],
              ["reports", "Exports"],
            ]).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => onOpenTab?.(key)}
                style={{ padding: "8px 12px", borderRadius: 10, border: "1px solid #e2e8f0", background: "#fff", fontFamily: "inherit", fontSize: 12, fontWeight: 700, color: "#334155", cursor: "pointer" }}
              >
                {label} →
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
